import { useMutation } from "@apollo/client/react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";

import { constantsData, identifiersData, namesData, statusTypesData } from "@/data";
import { deleteVideoCutMutation, getVideoCutsListQuery } from "@/graph";
import { openAlert } from "@/redux/actions";
import { displayErrorHandler } from "@/utils";

const useDeleteVideoCut = (videoId) => {
    const { t } = useTranslation();

    const dispatch = useDispatch();

    const [deleteVideoCutAction, { loading }] = useMutation(deleteVideoCutMutation, {
        awaitRefetchQueries: true,
        refetchQueries: [
            {
                query: getVideoCutsListQuery,
                variables: { id: videoId },
            },
        ],
    });

    const { deleteVideoCutPending: deleteVideoCutPendingToastName } = namesData.toasts;

    const { toast: toastId } = identifiersData.ids;

    const deleteVideoCut = async (videoCutId) => {
        try {
            await deleteVideoCutAction({
                onCompleted: () => {
                    dispatch(
                        openAlert(
                            t("messages.deleteMsgs.success", { entity: t("videos.entity") }),
                            statusTypesData.success,
                        ),
                    );
                },
                variables: {
                    where: { id: videoCutId },
                },
            });
        } catch (err) {
            displayErrorHandler(
                err.message === constantsData.failedToFetch ? t("messages.networkFailure") : err.message,
                dispatch,
                openAlert,
            );
        } finally {
            toast.dismiss(`${deleteVideoCutPendingToastName}_${toastId}`);
        }
    };

    useEffect(
        () => {
            if (loading) {
                toast.info(() => t("messages.deleteMsgs.pending", { entity: t("videos.entity") }), {
                    toastId: `${deleteVideoCutPendingToastName}_${toastId}`,
                });
            }
        },
        [loading], //eslint-disable-line
    );

    return { deleteVideoCut };
};

export default useDeleteVideoCut;
